import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import {
  FolderKanban,
  Layers,
  Activity,
  FileText,
  BadgePercent,
  Plug,
  Shield,
  DollarSign,
  Settings,
  Users,
  SlidersHorizontal,
  Table2,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useProjects, useArtifactCount } from "@/hooks/useApi";
import { useCurrentUser } from "@/hooks/useAuth";
import { navRoutesFor, type NavGroup, type NavRoutePath } from "@/lib/app-nav";
import {
  canReachProjectRoute,
  projectRoutePath,
  PROJECT_TABS,
  type ProjectTabSlug,
} from "@/lib/project-nav";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  count?: number | string;
  end?: boolean;
}

const labelVariants = {
  open: { opacity: 1, x: 0, width: "auto", transition: { duration: 0.2, ease: "easeOut" as const } },
  closed: { opacity: 0, x: -8, width: 0, transition: { duration: 0.15, ease: "easeIn" as const } },
};

function useActiveProjectId(): string | undefined {
  const { pathname } = useLocation();
  const m = pathname.match(/^\/projects\/([^/]+)/);
  return m?.[1];
}

/**
 * As sete sub-telas do projeto, já filtradas pelo papel de quem olha.
 *
 * O mínimo de cada uma vem do catálogo (`lib/project-nav.ts`) — o mesmo que as
 * rotas guardam. Antes disto a lista inteira aparecia para todo mundo: um `user`
 * via Runs, Artifacts, Connections, Cost e Settings do projeto, e o único jeito de
 * descobrir que não era para ele era clicar.
 *
 * O `slug` é a chave, e a URL sai de `projectRoutePath` em vez de ser montada à
 * mão aqui: link e rota não podem apontar para caminhos diferentes.
 */
function projectNav(
  projectId: string,
  role: string | null | undefined,
  t: (key: string) => string,
  artifactsCount: number | undefined,
): NavItem[] {
  const titles: Record<ProjectTabSlug, Omit<NavItem, "url">> = {
    "": { title: t("Overview"), icon: Layers, end: true },
    runs: { title: t("Runs"), icon: Activity },
    // `count` fica `undefined` (badge escondido, ver SidebarNavItem) até a
    // contagem real chegar — nunca um placeholder tipo 0 antes da hora.
    artifacts: { title: t("Artifacts"), icon: FileText, count: artifactsCount },
    connections: { title: t("Connections"), icon: Plug },
    calibration: { title: t("Calibration"), icon: SlidersHorizontal },
    "commercial-calibration": { title: t("Commercial calibration"), icon: BadgePercent },
    diagnostic: { title: t("Diagnostic"), icon: Shield },
    cost: { title: t("Cost & Tokens"), icon: DollarSign },
    settings: { title: t("Project settings"), icon: Settings },
  };

  return PROJECT_TABS.filter((tab) => canReachProjectRoute(role, tab.slug)).map((tab) => ({
    ...titles[tab.slug],
    url: projectRoutePath(projectId, tab.slug),
  }));
}

function SidebarNavItem({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={collapsed ? item.title : undefined}>
        <NavLink
          to={item.url}
          end={item.end}
          className={cn(
            "relative flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px]",
            "text-[#596b85] hover:bg-[#eef3f9] hover:text-[#08254c] transition-colors",
          )}
          activeClassName={cn(
            "bg-[#eef3f9] text-[#08254c] font-semibold",
            "before:content-[''] before:absolute before:left-[-10px] before:top-2 before:bottom-2",
            "before:w-[3px] before:bg-[#ffc83d] before:rounded-r-sm",
            "[&_.count]:bg-[rgba(255,200,61,0.2)] [&_.count]:text-[#8a6200]",
          )}
        >
          <item.icon className="h-4 w-4 shrink-0" />
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                key="label"
                variants={labelVariants}
                initial="closed"
                animate="open"
                exit="closed"
                className="overflow-hidden whitespace-nowrap flex-1"
              >
                {item.title}
              </motion.span>
            )}
          </AnimatePresence>
          {!collapsed && item.count != null && (
            <span className="count ml-auto text-[10px] font-mono bg-[#f2f4f7] text-[#7b8799] px-1.5 py-px rounded-full">
              {item.count}
            </span>
          )}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function Group({
  label,
  items,
  collapsed,
}: {
  label: string;
  items: NavItem[];
  collapsed: boolean;
}) {
  return (
    <SidebarGroup>
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <SidebarGroupLabel className="text-[10px] uppercase tracking-[0.1em] text-[#7b8799] font-semibold font-mono px-2.5">
              {label}
            </SidebarGroupLabel>
          </motion.div>
        )}
      </AnimatePresence>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => (
            <SidebarNavItem key={item.url} item={item} collapsed={collapsed} />
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

export function AppSidebar() {
  const { t } = useTranslation();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const projectId = useActiveProjectId();
  const me = useCurrentUser();
  const { data: projects } = useProjects();
  const { data: artifactsCount } = useArtifactCount(projectId);

  /**
   * Rótulo e ícone dos links que têm papel mínimo. O mínimo e o grupo moram no
   * catálogo (`lib/app-nav.ts`) — o mesmo que as rotas guardam; aqui fica só o
   * que é apresentação. Antes disto o mínimo estava escrito nos dois arquivos, e
   * eles divergiram.
   */
  const gatedNavTitles: Record<NavRoutePath, Omit<NavItem, "url">> = {
    "/admin/reference-data": { title: t("Reference Data"), icon: Table2 },
    "/admin/team": { title: t("Team"), icon: Users },
    "/admin/settings": { title: t("Settings"), icon: SlidersHorizontal },
    "/admin/integration": { title: t("Integration"), icon: Plug },
  };

  const gatedNav = (group: NavGroup): NavItem[] =>
    navRoutesFor(group, me?.role).map((path) => ({ ...gatedNavTitles[path], url: path }));

  /**
   * O grupo Workspace: Projects e, logo abaixo, Reference Data.
   *
   * Projects não tem mínimo — todo mundo autenticado o alcança, e por isso fica
   * fora do catálogo. Reference Data tem, e vem aqui e não no grupo Admin: é o
   * catálogo de dados que os agentes consomem, trabalho de quem executa o
   * projeto, não administração de workspace.
   *
   * ⚠️ **São só esses dois, e é de propósito.** `/dashboard`, `/agents` e
   * `/artifacts` estavam neste grupo e saíram na série SIMP (#422) — hoje são
   * redirect legado em `App.tsx`, e os módulos de API que os alimentavam foram
   * apagados. Não é uma lista pela metade esperando os itens de volta.
   */
  const workspaceNav: NavItem[] = [
    { title: t("Projects"), url: "/projects", icon: FolderKanban, end: true, count: projects?.length },
    ...gatedNav("workspace"),
  ];

  /**
   * O grupo Admin, filtrado pelo papel de quem está olhando.
   *
   * Antes ele aparecia inteiro para todo mundo: um `user` clicava em "Team", a
   * tela montava, disparava a chamada e só então tomava `403`. Agora quem não
   * alcança não vê o link — e o mínimo de cada um é o que o backend cobra.
   *
   * **Team fica em `manager`** — decisão de produto, não do backend. Fechar tudo
   * em `admin` tirava o manager, que é quem administra equipe no produto
   * (`POST /api/auth/register` já exige `manager`), e o roster desceu junto para
   * que a tela funcionasse para ele em vez de só aparecer.
   */
  const visibleAdminNav = gatedNav("admin");
  const visibleProjectNav = projectId
    ? projectNav(projectId, me?.role, t, artifactsCount)
    : [];

  return (
    <Sidebar collapsible="icon" className="border-r border-[#e0e6ed] bg-white">
      <SidebarContent className="pt-3">
        <Group label={t("Workspace")} items={workspaceNav} collapsed={collapsed} />
        {visibleProjectNav.length > 0 && (
          <Group label={t("This Project")} items={visibleProjectNav} collapsed={collapsed} />
        )}
        {visibleAdminNav.length > 0 && (
          <Group label={t("Admin")} items={visibleAdminNav} collapsed={collapsed} />
        )}
      </SidebarContent>
    </Sidebar>
  );
}
